// Numeri di telefono: regole pure, senza dipendenze, usate dallo schema del Lead,
// dalla ricerca nelle liste e dall'importatore Close.
const SEPARATORS = /[\s.\-/]/g;
const PARENTHESES = /[()]/g;
const PHONE_SHAPE = /^\+?\d*$/;
// «+41 (0)79…», «0044 (0)20…»: lo zero tra parentesi dopo il prefisso del paese è il prefisso interurbano, non una cifra.
const TRUNK_ZERO_AFTER_COUNTRY_CODE = /^(\+|00)(\d{1,3})\(0\)/;
const ITALY_PREFIX = "39";
const ITALY_NATIONAL_LENGTH = { min: 6, max: 11 };
const ITALY_MOBILE_LENGTH = { min: 9, max: 10 };
// La prima cifra nazionale di un cellulare e di un fisso italiano.
const ITALY_MOBILE_LEAD = "3";
const ITALY_LANDLINE_LEAD = "0";
// Con il prefisso 39 scritto senza "+", le cifre che seguono devono essere un numero italiano completo.
const ITALY_PREFIXED_LENGTH = { min: 9, max: 11 };
/**
 * Come `normalizePhone` riconosce un numero italiano (`classifyItalianNumber`): il prefisso del paese, la prima cifra
 * nazionale del cellulare e del fisso, la lunghezza delle cifre nazionali di ogni numero e quella di un cellulare. Le legge
 * anche la pre-selezione SQL di «Pulizia dati» (`DATA_ISSUE_CLEAN_SHAPES` di `data-quality.ts`), che deve riconoscere gli
 * stessi numeri senza rileggerli uno per uno.
 */
export const ITALIAN_NUMBER_RULES = Object.freeze({
    prefix: ITALY_PREFIX,
    mobileLead: ITALY_MOBILE_LEAD,
    landlineLead: ITALY_LANDLINE_LEAD,
    nationalLength: ITALY_NATIONAL_LENGTH,
    mobileLength: ITALY_MOBILE_LENGTH,
});
const SWITZERLAND_PREFIX = "41";
const SWITZERLAND_NATIONAL_LENGTH = 9;
const SWITZERLAND_MOBILE = /^7[5-9]/;
const OTHER_INTERNATIONAL_LENGTH = { min: 8, max: 15 };
function inRange(value, range) {
    return value >= range.min && value <= range.max;
}
function isPrefixedItalianNumber(digits) {
    if (!digits.startsWith(ITALY_PREFIX))
        return false;
    const national = digits.slice(ITALY_PREFIX.length);
    return inRange(national.length, ITALY_PREFIXED_LENGTH) && /^[03]/.test(national);
}
function fail(reason) {
    return Object.freeze({ ok: false, reason });
}
function freezePhone(phone) {
    return Object.freeze(phone);
}
function classifyItalianNumber(digits) {
    const national = digits.slice(ITALY_PREFIX.length);
    if (national.length < ITALY_NATIONAL_LENGTH.min)
        return fail("too_short");
    if (national.length > ITALY_NATIONAL_LENGTH.max)
        return fail("too_long");
    let kind = "unknown";
    if (national.startsWith(ITALY_MOBILE_LEAD) && inRange(national.length, ITALY_MOBILE_LENGTH))
        kind = "mobile";
    else if (national.startsWith(ITALY_LANDLINE_LEAD))
        kind = "landline";
    return freezePhone({ e164: `+${digits}`, country: "IT", kind, nationalDigits: national });
}
function classifySwissNumber(digits) {
    const national = digits.slice(SWITZERLAND_PREFIX.length);
    if (national.length < SWITZERLAND_NATIONAL_LENGTH)
        return fail("too_short");
    if (national.length > SWITZERLAND_NATIONAL_LENGTH)
        return fail("too_long");
    const kind = SWITZERLAND_MOBILE.test(national) ? "mobile" : "landline";
    return freezePhone({ e164: `+${digits}`, country: "CH", kind, nationalDigits: national });
}
function classifyOtherNumber(digits) {
    if (digits.length < OTHER_INTERNATIONAL_LENGTH.min)
        return fail("too_short");
    if (digits.length > OTHER_INTERNATIONAL_LENGTH.max)
        return fail("too_long");
    return freezePhone({ e164: `+${digits}`, country: null, kind: "unknown", nationalDigits: digits });
}
/**
 * Porta un numero scritto a mano in forma internazionale e dice che cosa è stato corretto.
 * Senza prefisso internazionale il numero è del paese di partenza (Italia, salvo diversa scelta di chi chiama).
 */
export function normalizePhone(raw, options = {}) {
    const defaultCountry = options.defaultCountry ?? "IT";
    const text = typeof raw === "string" ? raw : "";
    const corrections = [];
    const withoutSeparators = text.replace(SEPARATORS, "");
    const trunk = TRUNK_ZERO_AFTER_COUNTRY_CODE.exec(withoutSeparators);
    // Per l'Italia lo zero fa parte del numero (fisso): si tolgono soltanto le parentesi.
    const cleaned = trunk && trunk[2] !== ITALY_PREFIX
        ? `${trunk[1]}${trunk[2]}${withoutSeparators.slice(trunk[0].length)}`
        : withoutSeparators;
    const stripped = cleaned.replace(PARENTHESES, "");
    if (stripped.length !== text.length)
        corrections.push("removed_formatting");
    if (!stripped)
        return fail("empty");
    if (!PHONE_SHAPE.test(stripped))
        return fail("invalid_characters");
    let digits = stripped.startsWith("+") ? stripped.slice(1) : stripped;
    if (stripped.startsWith("+")) {
        // «+00…» vale come «00…»; ogni altro «+0…» non ha un prefisso di paese.
        if (digits.startsWith("00")) {
            digits = digits.slice(2);
            corrections.push("replaced_00_prefix");
        }
    }
    else if (digits.startsWith("00")) {
        digits = digits.slice(2);
        corrections.push("replaced_00_prefix");
    }
    else if (isPrefixedItalianNumber(digits)) {
        corrections.push("added_plus");
    }
    else if (defaultCountry === "IT" && /^[03]/.test(digits)) {
        digits = `${ITALY_PREFIX}${digits}`;
        corrections.push("added_country_prefix");
    }
    else if (defaultCountry === "CH" && digits.startsWith("0")) {
        digits = `${SWITZERLAND_PREFIX}${digits.slice(1)}`;
        corrections.push("added_country_prefix");
    }
    else {
        // Nessun paese di partenza riconosciuto: le cifre sono già un numero internazionale senza "+".
        corrections.push("added_plus");
    }
    // Nessun prefisso di paese comincia con 0: l'e164 è la chiave dei telefoni doppi e deve essere canonico.
    if (digits.startsWith("0"))
        return fail("invalid_characters");
    let classified;
    if (digits.startsWith(ITALY_PREFIX))
        classified = classifyItalianNumber(digits);
    else if (digits.startsWith(SWITZERLAND_PREFIX))
        classified = classifySwissNumber(digits);
    else
        classified = classifyOtherNumber(digits);
    if ("ok" in classified)
        return classified;
    return Object.freeze({ ok: true, phone: classified, corrections: Object.freeze(corrections) });
}
/**
 * Le cifre con cui si cerca un numero: forma internazionale senza "+" e forma nazionale, senza ripetizioni.
 * Per la Svizzera anche la forma con lo zero iniziale, come la scrive chi la chiama («079 123 45 67»).
 */
export function phoneSearchDigits(phone) {
    const forms = [phone.e164.slice(1), phone.nationalDigits];
    if (phone.country === "CH")
        forms.push(`0${phone.nationalDigits}`);
    return [...new Set(forms)];
}
//# sourceMappingURL=phones.js.map